import React, { useState, useEffect, useRef, useCallback } from 'react';
import './SlogenCarousel.css';

function SlogenCarousel({ items, autoPlayInterval = 6000, autoPlay = true }) {
  // 初始位置设置在中间的循环（第2个循环的第一张）
  const [currentIndex, setCurrentIndex] = useState(items.length * 2);
  const [gapSize, setGapSize] = useState(80); // 默认gap为80px
  const [isTablet, setIsTablet] = useState(false); // 是否为tablet屏幕
  const [isMobile, setIsMobile] = useState(false); // 是否为移动端
  const autoPlayRef = useRef(null);
  const trackRef = useRef(null);
  
  // 拖动相关状态
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartX, setDragStartX] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const [dragStartTime, setDragStartTime] = useState(0);
  const viewportRef = useRef(null);
  
  // 检测是否为英文内容
  const isEnglishContent = (text) => {
    // 检测文本中英文字符占比
    const englishChars = text.match(/[a-zA-Z]/g);
    return englishChars && englishChars.length > text.length * 0.5;
  };

  // 复制列表多次，创建假的无限循环（复制5次，足够长时间不会看到重置）
  const repeatCount = 5;
  const infiniteItems = Array(repeatCount).fill(items).flat();

  // 切换到下一张
  const handleNext = useCallback(() => {
    setCurrentIndex((prevIndex) => {
      const nextIndex = prevIndex + 1;
      // 当滚动到第4个循环的末尾时，准备重置回第2个循环的开头
      if (nextIndex >= items.length * 4) {
        // 等待当前过渡完成后重置到中间位置（第2个循环）
        setTimeout(() => {
          if (trackRef.current) {
            trackRef.current.style.transition = 'none';
            setCurrentIndex(items.length * 2);
            // 强制重绘
            requestAnimationFrame(() => {
              if (trackRef.current) {
                trackRef.current.style.transition = 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)';
              }
            });
          }
        }, 500);
      }
      return nextIndex;
    });
  }, [items.length]);

  // 重置自动播放计时器
  const resetAutoPlay = useCallback(() => {
    if (autoPlayRef.current) {
      clearInterval(autoPlayRef.current);
    }
    autoPlayRef.current = setInterval(() => {
      handleNext();
    }, autoPlayInterval);
  }, [handleNext, autoPlayInterval]);

  // 点击卡片切换（移动端禁用点击切换，使用拖动）
  const handleCardClick = (index) => {
    if (!isMobile && index !== currentIndex) {
      setCurrentIndex(index);
      if (autoPlay) {
        resetAutoPlay();
      }
    }
  };

  // 切换到指定索引
  const goToIndex = useCallback((index) => {
    setCurrentIndex((prevIndex) => {
      let targetIndex = index;
      // 当滚动到第4个循环的末尾时，准备重置回第2个循环的开头
      if (targetIndex >= items.length * 4) {
        setTimeout(() => {
          if (trackRef.current) {
            trackRef.current.style.transition = 'none';
            setCurrentIndex(items.length * 2);
            requestAnimationFrame(() => {
              if (trackRef.current) {
                trackRef.current.style.transition = 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)';
              }
            });
          }
        }, 500);
      }
      return targetIndex;
    });
    if (autoPlay) {
      resetAutoPlay();
    }
  }, [items.length, autoPlay, resetAutoPlay]);

  // 触摸开始
  const handleTouchStart = useCallback((e) => {
    if (!isMobile) return;
    setIsDragging(true);
    setDragStartX(e.touches[0].clientX);
    setDragStartTime(Date.now());
    setDragOffset(0);
    // 暂停自动播放
    if (autoPlayRef.current) {
      clearInterval(autoPlayRef.current);
    }
    // 禁用过渡动画
    if (trackRef.current) {
      trackRef.current.style.transition = 'none';
    }
  }, [isMobile]);

  // 触摸移动
  const handleTouchMove = useCallback((e) => {
    if (!isMobile || !isDragging) return;
    e.preventDefault();
    const currentX = e.touches[0].clientX;
    const offset = currentX - dragStartX;
    setDragOffset(offset);
  }, [isMobile, isDragging, dragStartX]);

  // 触摸结束
  const handleTouchEnd = useCallback(() => {
    if (!isMobile || !isDragging) return;
    
    const dragDistance = Math.abs(dragOffset);
    const dragDuration = Date.now() - dragStartTime;
    // 避免除零错误，如果时间太短则使用默认值
    const dragVelocity = dragDuration > 0 ? dragDistance / dragDuration : 0;
    
    // 恢复过渡动画
    if (trackRef.current) {
      trackRef.current.style.transition = 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)';
    }
    
    // 判断是否切换：拖动距离超过屏幕宽度的30% 或 拖动速度超过阈值
    const threshold = viewportRef.current ? viewportRef.current.offsetWidth * 0.3 : 100;
    const velocityThreshold = 0.5; // 速度阈值 (px/ms)
    
    if (dragDistance > threshold || (dragDuration > 0 && dragVelocity > velocityThreshold)) {
      if (dragOffset > 0) {
        // 向右拖动，切换到上一张
        goToIndex(currentIndex - 1);
      } else {
        // 向左拖动，切换到下一张
        goToIndex(currentIndex + 1);
      }
    } else {
      // 拖动距离不够，回弹到当前位置
      setDragOffset(0);
    }
    
    setIsDragging(false);
    setDragOffset(0);
    
    // 恢复自动播放
    if (autoPlay && items.length > 1) {
      resetAutoPlay();
    }
  }, [isMobile, isDragging, dragOffset, dragStartTime, currentIndex, goToIndex, autoPlay, items.length, resetAutoPlay]);

  // 自动播放
  useEffect(() => {
    if (autoPlay && items.length > 1) {
      autoPlayRef.current = setInterval(() => {
        handleNext();
      }, autoPlayInterval);

      return () => {
        if (autoPlayRef.current) {
          clearInterval(autoPlayRef.current);
        }
      };
    }
  }, [autoPlay, items.length, autoPlayInterval, handleNext]);

  // 当 items 变化时重置到中间位置
  useEffect(() => {
    setCurrentIndex(items.length * 2);
  }, [items]);

  // 监听窗口大小变化，动态设置gap值和屏幕类型
  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      
      if (width < 834) {
        // 移动端
        setGapSize(80);
        setIsMobile(true);
        setIsTablet(false);
      } else if (width >= 834 && width <= 1919) {
        // Tablet
        setGapSize(40);
        setIsMobile(false);
        setIsTablet(true);
      } else {
        // 桌面端
        setGapSize(80);
        setIsMobile(false);
        setIsTablet(false);
      }
    };

    // 初始化时设置一次
    updateScreenSize();

    // 监听窗口大小变化
    window.addEventListener('resize', updateScreenSize);

    return () => {
      window.removeEventListener('resize', updateScreenSize);
    };
  }, []);

  // 计算外层容器固定高度：检查所有items中是否有英文内容，如果有则固定为456px，否则336px
  const outerContainerHeight = isMobile 
    ? 'auto' 
    : (items.some(item => isEnglishContent(item.text)) ? '456px' : '336px');

  // 计算transform，考虑拖动偏移
  const calculateTransform = () => {
    const baseTransform = `translateX(calc(-${currentIndex * 100}% - ${currentIndex * gapSize}px))`;
    if (isMobile && isDragging) {
      return `translateX(calc(-${currentIndex * 100}% - ${currentIndex * gapSize}px + ${dragOffset}px))`;
    }
    return baseTransform;
  };

  return (
    <div className="slogen-carousel">
      <div 
        className="slogen-carousel-viewport"
        ref={viewportRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{ touchAction: 'pan-y', userSelect: 'none', height: outerContainerHeight }}
      >
        <div
          ref={trackRef}
          className="slogen-carousel-track"
          style={{
            transform: calculateTransform(),
            transition: isDragging ? 'none' : 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          {infiniteItems.map((item, index) => (
            <div
              key={index}
              className={`slogen-carousel-item ${
                index === currentIndex ? 'active' : ''
              } ${index < currentIndex ? 'prev' : ''} ${
                index > currentIndex ? 'next' : ''
              }`}
              onClick={() => handleCardClick(index)}
              style={{
                cursor: index !== currentIndex ? 'pointer' : 'default',
              }}
            >
              <div 
                className="slogen-card"
                style={{
                  // 移动端不设置高度，使用CSS的auto
                  height: isMobile 
                    ? undefined 
                    : (index === currentIndex 
                        ? (isEnglishContent(item.text) ? '456px' : '336px')
                        : (isEnglishContent(item.text) ? '424px' : '304px'))
                }}
              >
                <div className="slogen-card-content">
                  <div className="slogen-card-title">
                    <div
                      className="slogen-card-icon"
                      style={{ backgroundImage: `url(${item.icon})` }}
                    ></div>
                    <div>
                      <p 
                        className="slogen-card-title-text"
                        style={{
                          fontSize: (() => {
                            // 移动端统一使用18px
                            if (isMobile) {
                              return '18px';
                            }
                            // 如果是英文
                            if (isEnglishContent(item.text)) {
                              // tablet屏幕下使用20px，桌面端使用24px
                              return isTablet ? '20px' : '24px';
                            }
                            // 中文根据行数判断
                            const lines = item.text.split('\n');
                            const lineCount = lines.length;
                            if (lineCount === 2) return '24px';
                            if (lineCount === 3) return '24px';
                            return '24px'; // 4行及以上
                          })()
                        }}
                      >
                        {item.text.split('\n').map((line, i) => (
                          <React.Fragment key={i}>
                            {line}
                            {i < item.text.split('\n').length - 1 && <br />}
                          </React.Fragment>
                        ))}
                      </p>
                      <p className="slogen-card-title-author">{item.author}</p>
                    </div>
                  </div>
                  <div
                    className="slogen-card-image"
                    style={{ 
                      backgroundImage: `url(${item.image})`,
                    }}
                  ></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 导航指示器 */}
      {items.length > 1 && (
        <div className="slogen-carousel-indicators">
          {items.map((_, index) => {
            // 计算当前激活项对应的原始索引
            const activeOriginalIndex = currentIndex % items.length;
            // 判断是否应该高亮：当前激活项对应的原始索引等于指示器的index
            const isActive = activeOriginalIndex === index;
            
            // 点击指示器时，跳转到距离当前currentIndex最近的对应index的倍数位置
            const handleIndicatorClick = () => {
              // 计算距离当前currentIndex最近的、对应index的倍数位置
              // 目标位置应该是：index + items.length * n，其中n是某个整数
              // 找到距离currentIndex最近的n值
              const n = Math.round((currentIndex - index) / items.length);
              const targetIndex = index + items.length * n;
              goToIndex(targetIndex);
            };
            
            return (
              <button
                key={index}
                className={`slogen-carousel-indicator ${
                  isActive ? 'active' : ''
                }`}
                onClick={handleIndicatorClick}
                aria-label={`跳转到第 ${index + 1} 张卡片`}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

export default SlogenCarousel;

